/**
 * Block / flat / floor helpers for apartment addresses.
 *
 * Mirrors DD-backend/Helper/addressBlocks.js. The server is the authority — it
 * re-validates and re-derives everything on save — but the form needs the same
 * rules locally to pre-select a block button, show the derived floor as the
 * user types, and avoid a round-trip just to say "that isn't a block here".
 *
 * Keep the two files in step. If you change a rule here, change it there.
 */

/** Hub type -> the address form that should open for it. */
export const ADDRESS_TYPE_BY_HUB_TYPE = {
  apartment: "Apartment",
  school: "School",
  office: "Work",
  home: "Home",
};

/** Address types that carry a block / flat / floor. */
export const BLOCK_ADDRESS_TYPES = ["Apartment", "PG"];

/** Does this address type need a block and flat? */
export function usesBlocks(addressType) {
  return BLOCK_ADDRESS_TYPES.includes(addressType);
}

/**
 * Which form should open once the hub is known.
 *
 * Deliberately a suggestion, not a lock — the caller still lets the customer
 * switch. A hub covers a polygon rather than a single building, so a genuine
 * standalone house can sit inside an apartment hub's area.
 */
export function addressTypeForHub(hub) {
  return ADDRESS_TYPE_BY_HUB_TYPE[hub?.hubType] || "Home";
}

/** A block is valid only if it exactly matches one the admin defined. */
export function isBlockClean(towerBlock, hubBlocks) {
  if (!Array.isArray(hubBlocks) || hubBlocks.length === 0) return false;
  return hubBlocks.includes(String(towerBlock || "").trim());
}

/** A flat is valid when it is 3-4 digits: 605, 1108, 002. */
export function isFlatClean(flat) {
  return /^\d{3,4}$/.test(String(flat || "").trim());
}

/**
 * Floor from flat: last two digits are the unit, the rest is the floor.
 * "605" -> 6, "1108" -> 11, "002" -> 0. Returns null when unparseable.
 *
 * The customer never types this. When they did, the same column collected
 * "7th floor", "2 nd", "Ground", "A" and "304".
 */
export function floorFromFlat(flat) {
  const value = String(flat || "").trim();
  if (!isFlatClean(value)) return null;
  return parseInt(value.slice(0, value.length - 2), 10);
}

/** Display form of a derived floor. 0 reads as "Ground". */
export function floorLabel(floorNumber) {
  if (floorNumber === null || floorNumber === undefined) return "";
  return floorNumber === 0 ? "Ground" : String(floorNumber);
}

/** Rider walking order = the admin's ordering of the block list. */
export function blockSortIndex(towerBlock, hubBlocks) {
  if (!Array.isArray(hubBlocks)) return Number.MAX_SAFE_INTEGER;
  const index = hubBlocks.indexOf(String(towerBlock || "").trim());
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

const reduceBlock = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\b(block|blk|blok|tower|twr|wing)\b/g, " ")
    .replace(/[^a-z0-9]+/g, "");

/**
 * Guess which block some legacy free text meant, so the popup can pre-select a
 * button. Only ever a suggestion — the customer confirms before it is saved.
 */
export function guessBlock(rawText, hubBlocks) {
  if (!Array.isArray(hubBlocks) || hubBlocks.length === 0) return null;
  const text = String(rawText || "").trim();
  if (!text) return null;

  if (hubBlocks.includes(text)) return text;

  const lower = text.toLowerCase();
  const caseHit = hubBlocks.find((b) => b.toLowerCase() === lower);
  if (caseHit) return caseHit;

  // "G block" / "Block G" / "g-block" all reduce to "g".
  const target = reduceBlock(text);
  if (target) {
    const reducedHit = hubBlocks.find((b) => reduceBlock(b) === target);
    if (reducedHit) return reducedHit;
  }

  // Legacy values with the flat glued on: "G-304", "H 806", "E705".
  const withoutFlat = reduceBlock(text.replace(/\d{3,4}\s*$/, ""));
  if (withoutFlat) {
    const flatHit = hubBlocks.find((b) => reduceBlock(b) === withoutFlat);
    if (flatHit) return flatHit;
  }

  // A block named inside a longer sentence: "Alpine pyramid g block 506".
  // The letter/digit split matters — people write the block glued to the flat
  // ("F806", "D602", "G002"), which would otherwise tokenise as one word.
  const haystack = ` ${lower
    .replace(/([a-z])(\d)/g, "$1 $2")
    .replace(/[^a-z0-9]+/g, " ")} `;
  const byLength = [...hubBlocks].sort((a, b) => b.length - a.length);
  for (const block of byLength) {
    const needle = block.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    if (needle && haystack.includes(` ${needle} `)) return block;
  }

  return null;
}

/** Rejects pincodes and other digit runs that cannot be a flat number. */
function isPlausibleFlat(value) {
  if (!/^\d{3,4}$/.test(value)) return false;
  return parseInt(value.slice(0, value.length - 2), 10) <= 40;
}

/**
 * Pull a flat number out of legacy free text.
 *
 * Never reads `fullAddress` — it is geocoded, so it carries the pincode
 * (560097) and Google's premise codes (D-003) and yields confident-looking
 * garbage like flat "0097".
 */
export function guessFlat(rawText) {
  const cleaned = String(rawText || "").replace(/[^A-Za-z0-9]+/g, " ");
  if (!cleaned.trim()) return null;

  const glued = cleaned.match(/(?:^|\s)[A-Za-z]\s?(\d{3,4})(?:\s|$)/);
  if (glued && isPlausibleFlat(glued[1])) return glued[1];

  const matches = cleaned.match(/(?:^|\s)\d{3,4}(?=\s|$)/g) || [];
  for (const match of matches) {
    const digits = match.trim();
    if (isPlausibleFlat(digits)) return digits;
  }
  return null;
}

/**
 * The single rule: does this address need the one-time confirmation popup, and
 * what should it be pre-filled with?
 *
 * There is no "already shown" flag anywhere. The check reads the address's own
 * data, so once the customer saves through the picker the answer flips to false
 * permanently and they are never asked again.
 */
export function inspectAddress(address, hubBlocks) {
  if (!address) return { needsFix: false };

  const blockOk = isBlockClean(address.towerBlock, hubBlocks);
  const flatOk = isFlatClean(address.flat);

  // Legacy data hides the real block/flat in whichever box the user could
  // reach: homeName for Home addresses, landmark for both, towerBlock for PG,
  // and — in live data — the flat field itself ("G705", "F203", "H 806").
  const haystack = [
    address.towerBlock,
    address.homeName,
    address.landmark,
    address.flat,
  ]
    .filter(Boolean)
    .join(" ");

  return {
    needsFix: !blockOk || !flatOk,
    blockOk,
    flatOk,
    suggestedBlock: blockOk
      ? String(address.towerBlock).trim()
      : guessBlock(haystack, hubBlocks),
    suggestedFlat: flatOk ? String(address.flat).trim() : guessFlat(haystack),
  };
}
